import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Camera } from "lucide-react";
import { GALLERY_IMAGES } from "@/lib/data";

export default function Gallery() {
  const [idx, setIdx] = useState(null);
  const open = idx !== null;

  const close = () => setIdx(null);
  const prev = (e) => {
    e?.stopPropagation();
    setIdx((i) => (i - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
  };
  const next = (e) => {
    e?.stopPropagation();
    setIdx((i) => (i + 1) % GALLERY_IMAGES.length);
  };

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-bold mb-4">
              <Camera className="w-3.5 h-3.5" /> Life at Globe Vista
            </div>
            <h2 className="text-4xl sm:text-5xl font-light tracking-tighter">
              Inside our <span className="font-serif italic text-gradient-royal">Santosh Nagar</span> classrooms.
            </h2>
          </div>
          <p className="text-muted-foreground max-w-md">
            Real classes, real students, real results. A glimpse of what happens every day at our Hyderabad centre.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {GALLERY_IMAGES.map((img, i) => (
            <motion.button
              key={img.src}
              onClick={() => setIdx(i)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              data-testid={`gallery-thumb-${i}`}
              className={`group relative overflow-hidden rounded-2xl md:rounded-3xl ${
                i === 0 ? "col-span-2 row-span-2 aspect-square" : "aspect-[4/5]"
              }`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-70 group-hover:opacity-90 transition-opacity" />
              <div className="absolute bottom-3 left-3 right-3 text-left">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#FCD34D]">Globe Vista</div>
                <div className="text-white text-sm md:text-base font-heading font-medium leading-tight mt-1">
                  {img.caption}
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
            data-testid="gallery-lightbox"
          >
            <button
              onClick={close}
              data-testid="gallery-lightbox-close"
              aria-label="Close"
              className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
            >
              <X className="w-5 h-5" />
            </button>
            <button
              onClick={prev}
              aria-label="Previous"
              data-testid="gallery-prev"
              className="absolute left-3 md:left-8 w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              aria-label="Next"
              data-testid="gallery-next"
              className="absolute right-3 md:right-8 w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            <motion.img
              key={idx}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              src={GALLERY_IMAGES[idx].src}
              alt={GALLERY_IMAGES[idx].alt}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl"
            />
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-sm tracking-wide">
              {GALLERY_IMAGES[idx].caption} · {idx + 1} / {GALLERY_IMAGES.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
